/**
 * Team step of the migrate wizard — pick existing resources and their role on the project.
 * Same building blocks as the per-project team UI: ResourcePicker (Clockify-synced directory) +
 * the shared PROJECT_ROLE_VALUES vocabulary. Capacity is out of MVP scope and is never collected.
 */
import { Plus, Trash2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { ResourcePicker } from "@/components/resources/ResourcePicker";
import {
  PROJECT_ROLE_VALUES,
  projectRoleLabel,
  type ProjectRole,
} from "@/types/intake";
import { requiresAssignable, type FieldErrors, type TeamRow } from "@/lib/migration";

export interface TeamStepProps {
  team: TeamRow[];
  onChange: (team: TeamRow[]) => void;
  errors: FieldErrors;
}

let rowSeq = 0;
function newRow(role: ProjectRole): TeamRow {
  rowSeq += 1;
  return { key: `row-${Date.now()}-${rowSeq}`, resourceId: "", resourceName: "", role };
}

export function TeamStep({ team, onChange, errors }: TeamStepProps): JSX.Element {
  const update = (index: number, patch: Partial<TeamRow>): void => {
    onChange(team.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };
  const remove = (index: number): void => {
    onChange(team.filter((_, i) => i !== index));
  };
  const add = (): void => {
    const hasPm = team.some((r) => r.role === "Project Manager");
    const hasSa = team.some((r) => r.role === "Solution Architect");
    onChange([
      ...team,
      newRow(!hasPm ? "Project Manager" : !hasSa ? "Solution Architect" : "Engineer"),
    ]);
  };

  return (
    <div className="space-y-3">
      <p className="text-sm text-text-secondary">
        Add the people already working on this project. They're added to the linked Slack channel,
        Jira project and GitHub repo after the migration (best effort — use “Sync access” on the
        project if anyone is missed).
      </p>
      {team.length === 0 && (
        <p className="rounded-md border border-dashed border-outlines p-4 text-center text-sm text-text-secondary">
          No team members yet. You can also add them later from the project page.
        </p>
      )}
      <ul className="space-y-3" aria-label="Team members">
        {team.map((row, i) => {
          const personId = `mig-team-${i}-person`;
          const roleId = `mig-team-${i}-role`;
          const rowError = errors[`team.${i}`];
          return (
            <li
              key={row.key}
              className="grid grid-cols-1 gap-2 rounded-md border border-outlines p-3 sm:grid-cols-[1fr_1fr_auto] sm:items-end"
            >
              <div className="space-y-1.5">
                <Label htmlFor={personId}>Person</Label>
                <ResourcePicker
                  id={personId}
                  value={row.resourceId}
                  onChange={(resourceId, resourceName) =>
                    update(i, { resourceId, resourceName })
                  }
                  placeholder="Select a person…"
                  requireAssignable={requiresAssignable(row.role)}
                  // 2026-10-22: list only people whose title fits this row's role (server-side).
                  roleOnProject={row.role}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor={roleId}>Role on project</Label>
                <Select
                  id={roleId}
                  value={row.role}
                  onChange={(v) => update(i, { role: v as ProjectRole })}
                  options={PROJECT_ROLE_VALUES.map((r) => ({
                    value: r,
                    label: projectRoleLabel(r),
                  }))}
                />
              </div>
              <Button
                type="button"
                variant="ghost"
                onClick={() => remove(i)}
                aria-label={`Remove team member ${i + 1}${row.resourceName ? ` (${row.resourceName})` : ""}`}
              >
                <Trash2 className="h-4 w-4" aria-hidden="true" />
              </Button>
              {rowError && (
                <p role="alert" className="text-sm text-error sm:col-span-3">
                  {rowError}
                </p>
              )}
            </li>
          );
        })}
      </ul>
      {errors.team && (
        <p role="alert" className="text-sm text-error">
          {errors.team}
        </p>
      )}
      <Button type="button" variant="outline" onClick={add} disabled={team.length >= 50}>
        <Plus className="h-4 w-4" aria-hidden="true" />
        Add team member
      </Button>
    </div>
  );
}

export default TeamStep;
